<template>
  <div class="flow-empty-state">
    <div class="flow-empty-state__content">
      <div class="flow-empty-state__icon" :class="`flow-empty-state__icon--${type}`">
        <i :class="iconClass" />
      </div>
      <h2 class="flow-empty-state__title">{{ title }}</h2>
      <p class="flow-empty-state__description">{{ description }}</p>
      <button
        class="flow-empty-state__button"
        @click="handleStart"
      >
        <i class="fa-regular fa-play" />
        <span>{{ buttonLabel }}</span>
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, PropType } from 'vue';
import { useI18n } from 'vue-i18n';
import { TypeCampaignEnum } from '@/types';
import type { FlowStructureType } from '@/types/flow-structure';

export default defineComponent({
  name: 'FlowEmptyState',

  props: {
    type: {
      type: String as PropType<FlowStructureType>,
      required: true,
    },
  },

  emits: ['start-flow'],

  setup(props, { emit }) {
    const { t } = useI18n();

    const iconClass = computed(() => {
      return props.type === TypeCampaignEnum.BULK
        ? 'fa-regular fa-users'
        : 'fa-regular fa-bolt';
    });

    const title = computed(() => {
      return props.type === TypeCampaignEnum.BULK
        ? t('flows.emptyState.bulk.title')
        : t('flows.emptyState.automation.title');
    });

    const description = computed(() => {
      return props.type === TypeCampaignEnum.BULK
        ? t('flows.emptyState.bulk.description')
        : t('flows.emptyState.automation.description');
    });

    const buttonLabel = computed(() => {
      return props.type === TypeCampaignEnum.BULK
        ? t('flows.emptyState.bulk.button')
        : t('flows.emptyState.automation.button');
    });

    const handleStart = () => {
      emit('start-flow', props.type);
    };

    return {
      iconClass,
      title,
      description,
      buttonLabel,
      handleStart,
    };
  },
});
</script>

<style lang="scss" scoped>
.flow-empty-state {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  &__content {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    max-width: 400px;
    padding: 2rem;
  }

  &__icon {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 1.5rem;
    font-size: 1.75rem;

    &--bulk {
      background: rgba(139, 195, 74, 0.1);
      color: #8bc34a;
    }

    &--automation {
      background: rgba(33, 150, 243, 0.1);
      color: #2196f3;
    }
  }

  &__title {
    font-size: 1.25rem;
    font-weight: 600;
    color: #1f2937;
    margin: 0 0 0.5rem 0;
  }

  &__description {
    font-size: 0.875rem;
    color: #6b7280;
    margin: 0 0 1.5rem 0;
    line-height: 1.5;
  }

  &__button {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1.5rem;
    background: #8bc34a;
    color: #fff;
    border: none;
    border-radius: 0.375rem;
    font-size: 0.9375rem;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.2s ease, transform 0.1s ease;

    &:hover {
      background: #7cb342;
    }

    &:active {
      transform: scale(0.98);
    }

    i {
      font-size: 0.875rem;
    }
  }
}
</style>
